import { getValidAccessToken } from '../../lib/supabase';
import { acceptChatSendResponse, recoverChatSendReceipt } from './chatSendReceipt';
import {
  startWhatsappEmbeddedSignup,
  takeWhatsappSignupReturn,
  type CoexistenceSignup,
} from './whatsappEmbeddedSignup';

export { startWhatsappEmbeddedSignup, takeWhatsappSignupReturn };
export type { CoexistenceSignup };

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export type CoexistenceConnectResult = {
  name: string;
  displayNumber: string;
  warnings: string[];
  coexistence: boolean;
};

async function postCloudApi(body: Record<string, unknown>, onAccepted?: () => void): Promise<Record<string, unknown>> {
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Configuração do Supabase ausente.');
  const token = await getValidAccessToken();
  let response: Response;
  try {
  response = await fetch(`${supabaseUrl}/functions/v1/whatsapp-cloud-api`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: supabaseAnonKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  } catch (error) {
    if (typeof body.requestId === 'string') return recoverChatSendReceipt(body.requestId, onAccepted);
    throw error;
  }
  if (response.status >= 500 && typeof body.requestId === 'string') return recoverChatSendReceipt(body.requestId, onAccepted);
  const data = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok || data.error) {
    throw new Error(data.error || 'Falha na API oficial do WhatsApp');
  }
  return typeof body.requestId === 'string' ? acceptChatSendResponse(data, body.requestId, onAccepted) : data;
}

function warningsFrom(data: Record<string, unknown>): string[] {
  return Array.isArray(data.warnings) ? data.warnings.map(String) : [];
}

function messageIdFrom(data: Record<string, unknown>): string | undefined {
  return typeof data.messageId === 'string' ? data.messageId : undefined;
}

function postScopedCloudApi(
  action: string,
  organizationId: string,
  integrationId: string,
  fields: Record<string, unknown> = {},
  onAccepted?: () => void,
): Promise<Record<string, unknown>> {
  return postCloudApi({
    action,
    organization_id: organizationId,
    integration_id: integrationId,
    ...fields,
    ...(action.startsWith('send-') ? { requestId: fields.requestId || crypto.randomUUID() } : {}),
  }, onAccepted);
}

/**
 * Abre o Embedded Signup de coexistência.
 * O código OAuth vai direto para a Edge Function. O token da conta não passa pelo navegador.
 */
export async function connectWhatsappCoexistence(
  organizationId: string,
  signup: CoexistenceSignup,
): Promise<CoexistenceConnectResult> {
  const result = await postCloudApi({
    action: 'connect',
    organization_id: organizationId,
    code: signup.code,
    redirect_uri: signup.redirectUri,
  });

  return {
    name: String(result.name || 'WhatsApp'),
    displayNumber: String(result.displayNumber || ''),
    warnings: warningsFrom(result),
    coexistence: result.coexistence === true,
  };
}

export async function sendWhatsappCloudText(params: {
  requestId?: string;
  onAccepted?: () => void;
  organizationId: string;
  integrationId: string;
  to: string;
  text: string;
  replyToMessageId?: string;
}): Promise<{ messageId?: string }> {
  const data = await postScopedCloudApi('send-text', params.organizationId, params.integrationId, {
    to: params.to,
    text: params.text,
    requestId: params.requestId,
    reply_to_message_id: params.replyToMessageId,
  }, params.onAccepted);
  return { messageId: messageIdFrom(data) };
}

export async function sendWhatsappCloudMedia(params: {
  requestId?: string;
  onAccepted?: () => void;
  organizationId: string;
  integrationId: string;
  to: string;
  kind: 'image' | 'audio' | 'video' | 'document';
  link: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
  replyToMessageId?: string;
}): Promise<{ messageId?: string }> {
  const data = await postScopedCloudApi('send-media', params.organizationId, params.integrationId, {
    to: params.to,
    kind: params.kind,
    requestId: params.requestId,
    link: params.link,
    caption: params.caption,
    filename: params.filename,
    mime_type: params.mimeType,
    reply_to_message_id: params.replyToMessageId,
  }, params.onAccepted);
  return { messageId: messageIdFrom(data) };
}

export async function disconnectWhatsappCloud(
  organizationId: string,
  integrationId: string,
): Promise<{ warning?: string }> {
  const data = await postScopedCloudApi('disconnect', organizationId, integrationId);
  return { warning: typeof data.warning === 'string' && data.warning ? data.warning : undefined };
}

export async function removeWhatsappCloud(organizationId: string, integrationId: string): Promise<void> {
  await postScopedCloudApi('remove', organizationId, integrationId);
}

export async function repairWhatsappCloudWebhook(
  organizationId: string,
  integrationId: string,
): Promise<{ warning?: string }> {
  const data = await postScopedCloudApi('repair-webhook', organizationId, integrationId);
  return { warning: typeof data.warning === 'string' && data.warning ? data.warning : undefined };
}

export async function syncWhatsappCloud(
  organizationId: string,
  integrationId: string,
): Promise<{ warnings: string[] }> {
  const data = await postScopedCloudApi('sync', organizationId, integrationId);
  return { warnings: warningsFrom(data) };
}
